/**
 * Selection Transformer - Converts user selections to Equipment Register format
 * Implements user-controlled equipment registration workflow
 */

import { getHardcodedVesselRequirements } from '../../config/hardcodedVesselRequirements';
import { getCanonicalRequirements } from '../requirements/canonicalRequirements';
import { getFFEGuidance } from '../../config/guidance/ffe.guidance';
import { getLSAGuidance } from '../../config/guidance/lsa.guidance';
import { getNAVGuidance } from '../../config/guidance/nav.guidance';
import { getGMDSSGuidance } from '../../config/guidance/gmdss.guidance';
import { getElectricalGuidance } from '../../config/guidance/electrical.guidance';
import { getMachineryGuidance } from '../../config/guidance/machinery.guidance';
import { getGeneralGuidance } from '../../config/guidance/general.guidance';

// Import rowId function for consistent ID generation - includes sourceRequirementId to prevent duplicates
const rowId = ({ vesselId, type, location, variant = 'base', sourceRequirementId }) =>
  sourceRequirementId ? 
    `reg:${vesselId}:${(sourceRequirementId || '').toLowerCase().trim()}:${(variant || 'base').toLowerCase().trim()}` :
    `reg:${vesselId}:${(type || '').toLowerCase().trim()}:${(location || 'general').toLowerCase().trim()}:${(variant || 'base').toLowerCase().trim()}`;

// Cache for canonical requirements lookup
let canonicalRequirementsCache = null;

/**
 * Find canonical requirement by ID from Step 7 source
 * @param {string} requirementId - Requirement ID to find
 * @param {Object} vesselConfig - Vessel configuration for context
 * @returns {Object|null} Canonical requirement object
 */
const findCanonicalRequirement = (requirementId, vesselConfig = null) => {
  if (!canonicalRequirementsCache && vesselConfig) {
    canonicalRequirementsCache = getCanonicalRequirements(vesselConfig);
  }
  
  if (canonicalRequirementsCache) {
    return canonicalRequirementsCache.find(req => req.id === requirementId) || null;
  }
  
  return null;
};

/**
 * Transform user equipment selections to Equipment Register format
 * @param {Object} selections - User selections with required/optional arrays
 * @param {string} vesselId - Current vessel ID
 * @param {Object} vesselConfig - Vessel configuration for canonical requirements lookup
 * @returns {Array} Equipment register items
 */
export const transformSelectionsToRegister = (selections, vesselId, vesselConfig = null) => {
  if (!selections || !vesselId) return [];
  
  // Reset cache for new vessel config
  if (vesselConfig) {
    canonicalRequirementsCache = null;
  }
  
  const registerItems = [];
  
  // Process required items (always pre-selected)
  if (selections.required && Array.isArray(selections.required)) {
    selections.required.forEach(selection => {
      if (selection.selected && selection.qtyPresent > 0) {
        const item = createRegisterItem(selection, vesselId, 'required', vesselConfig);
        if (item) registerItems.push(item);
      }
    });
  }
  
  // Process optional items (user-toggled)
  if (selections.optional && Array.isArray(selections.optional)) {
    selections.optional.forEach(selection => {
      if (selection.selected && selection.qtyPresent > 0) {
        const item = createRegisterItem(selection, vesselId, 'optional', vesselConfig);
        if (item) registerItems.push(item);
      }
    });
  }
  
  if (process.env.NODE_ENV === 'development') {

  }
  
  // Log FFE items specifically
  if (process.env.NODE_ENV === 'development') {
    const ffeItems = registerItems.filter(item => item.category === 'FFE');


  }
  
  // MIGRATION CHECK: Log what keys exist in localStorage
  if (process.env.NODE_ENV === 'development') {
    const ffeItems = registerItems.filter(item => item.category === 'FFE');
    const vesselKeys = Object.keys(localStorage).filter(key => 
      key.includes('equipment_register_') && key.includes(vesselId)
    );
    if (vesselKeys.length > 0) {

    }
    
    // Check for gas detector specifically
    const gasDetector = ffeItems.find(item => item.sourceRequirementId === 'ffe_detector_gas_galley');
    if (gasDetector) {

    } else {

      // Check if it was in selections
      const gasSelection = [...(selections.required || []), ...(selections.optional || [])]
        .find(s => s.id === 'ffe_detector_gas_galley');
      if (gasSelection) {

      } else {

      }
    }
  }
  
  return registerItems;
};

/**
 * Create equipment register item from user selection
 * @param {Object} selection - User selection object
 * @param {string} vesselId - Current vessel ID  
 * @param {string} type - Selection type (required/optional)
 * @param {Object} vesselConfig - Vessel configuration for canonical requirements lookup
 * @returns {Object|null} Equipment register item
 */
const createRegisterItem = (selection, vesselId, type, vesselConfig = null) => {
  if (!selection.id) {
    if (process.env.NODE_ENV === 'development') {

    }
    return null;
  }
  
  // Extract type and location for consistent ID generation
  const itemType = getItemType(selection.id);
  const itemLocation = getItemLocation(selection);
  
  // Get WBC3 reference from canonical requirement
  const canonicalReq = findCanonicalRequirement(selection.id, vesselConfig);
  const regulationReference = canonicalReq?.reference || null;
  
  return {
    // Core identification using consistent rowId format
    id: rowId({ vesselId, type: itemType, location: itemLocation, variant: 'base', sourceRequirementId: selection.id }),
    sourceRequirementId: selection.id,
    
    // Display information
    name: getItemDisplayName(selection),
    category: selection.domain ? getDomainCategory(selection.domain) : getItemCategory(selection.id),
    type: getItemType(selection.id),
    location: getItemLocation(selection),
    
    // Regulatory reference from canonical requirements (Step 7 source)
    reference: regulationReference,
    regulation: regulationReference, // Backward compatibility
    
    // Quantities and status
    qtyPresent: selection.qtyPresent || 1,
    qtyRequired: selection.qtyRequired || (type === 'required' ? 1 : 0),
    required: type === 'required',
    
    // Source tracking
    source: 'user_selection',
    selectedAt: selection.selectedAt || new Date().toISOString(),
    
    // User fields (preserved from existing equipment)
    serialNo: selection.serialNo || null,
    installedAt: selection.installedAt || null,
    lastServiceAt: selection.lastServiceAt || null,
    nextCheckDue: selection.nextCheckDue || calculateNextCheckDue(selection.lastServiceAt, selection.id),
    notes: selection.notes || null,
    units: selection.units || [],
    photos: selection.photos || [],
    customFields: selection.customFields || {},
    
    // Guidance key assignment
    guidanceKey: getGuidanceKey(selection.id),
    
    // Metadata
    sourceVersion: '1.0.0',
    generatedAt: new Date().toISOString()
  };
};

/**
 * Get display name for equipment item
 * @param {Object} selection - User selection
 * @returns {string} Display name
 */
const getItemDisplayName = (selection) => {
  if (selection.name) return selection.name;
  if (selection.label) return selection.label;
  
  // Generate name from requirement ID
  const id = selection.id;
  
  // Basic name mapping for common items
  const nameMap = {
    'ffe_extinguisher_control_position': 'Portable Extinguisher - Control Position',
    'ffe_extinguisher_galley': 'Portable Extinguisher - Galley',
    'ffe_extinguisher_engine': 'Portable Extinguisher - Engine',
    'ffe_fire_blanket_risk_area': 'Fire Blanket',
    'ffe_fixed_system_engine': 'Fixed Fire System - Engine',
    'ffe_fixed_system_galley': 'Fixed Fire System - Galley',
    'ffe_fire_pump_required': 'Fire Pump',
    'ffe_fire_hose_fitted': 'Fire Hose',
    // Detectors
    'ffe_detector_fire_galley': 'Fire Detector - Galley',
    'ffe_detector_co_galley': 'CO Detector - Galley',
    'ffe_detector_gas_galley': 'Hydrocarbon Gas Detector - Galley'
  };
  
  return nameMap[id] || humanizeRequirementId(id);
};

/**
 * Convert requirement ID to human readable name
 * @param {string} id - Requirement ID
 * @returns {string} Human readable name
 */
const humanizeRequirementId = (id) => {
  return id
    .replace(/^ffe_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Convert domain key to proper category name
 * @param {string} domain - Domain key (ffe, lsa, navigation, etc.)
 * @returns {string} Category name
 */
const getDomainCategory = (domain) => {
  const domainMap = {
    'ffe': 'FFE',
    'lsa': 'LSA', 
    'navigation': 'Navigation',
    'gmdss': 'GMDSS',
    'machinery': 'Machinery',
    'medical': 'Medical',
    'electrical': 'Electrical',
    'environmental': 'Environmental',
    'operations': 'Operations',
    'crew': 'Crew',
    'misc': 'General'
  };
  
  return domainMap[domain] || 'General';
};

/**
 * Calculate next check due date based on equipment type and last service
 * @param {string} lastServiceAt - Last service date (ISO string)
 * @param {string} equipmentId - Equipment ID for determining interval
 * @returns {string|null} Next check due date (ISO string) or null if no service history
 */
const calculateNextCheckDue = (lastServiceAt, equipmentId) => {
  if (!lastServiceAt || !equipmentId) return null;
  
  const lastService = new Date(lastServiceAt);
  if (isNaN(lastService.getTime())) return null;
  
  // Default service intervals (in days) based on equipment type
  const serviceIntervals = {
    // Fire Fighting Equipment
    'ffe_extinguisher': 365,     // Annual
    'fire_extinguisher': 365,
    'extinguisher': 365,
    'ffe_fire_blanket': 365,
    'fire_blanket': 365,
    'ffe_fire_pump': 30,         // Monthly
    'fire_pump': 30,
    
    // Life Saving Appliances
    'lsa_lifebuoy': 30,          // Monthly
    'lifebuoy': 30,
    'lsa_lifejacket': 30,        // Monthly
    'lifejacket': 30,
    'lsa_liferaft': 365,         // Annual
    'liferaft': 365,
    
    // Default intervals
    'default': 365               // Annual for unknown equipment
  };
  
  // Find matching interval
  let interval = serviceIntervals['default'];
  
  // Try exact match first
  if (serviceIntervals[equipmentId]) {
    interval = serviceIntervals[equipmentId];
  } else {
    // Try partial matches
    const idLower = equipmentId.toLowerCase();
    for (const [key, days] of Object.entries(serviceIntervals)) {
      if (key !== 'default' && idLower.includes(key.split('_').pop())) {
        interval = days;
        break;
      }
    }
  }
  
  // Calculate next due date
  const nextDue = new Date(lastService);
  nextDue.setDate(nextDue.getDate() + interval);
  
  return nextDue.toISOString().split('T')[0]; // Return just YYYY-MM-DD
};

/**
 * Get category for equipment item
 * @param {string} id - Requirement ID
 * @returns {string} Category
 */
const getItemCategory = (id) => {
  if (!id) return 'General';
  
  // Direct prefix matching
  if (id.startsWith('ffe_')) return 'FFE';
  if (id.startsWith('lsa_') || id.startsWith('lsa.')) return 'LSA'; 
  if (id.startsWith('nav_') || id.startsWith('nav.')) return 'Navigation';
  if (id.startsWith('gmdss_') || id.startsWith('gmdss.')) {
    // CRITICAL: EPIRB/PLB/SART from GMDSS requirements belong to LSA
    // Per WBC3 17.5.3 and 14.12, these items belong to LSA domain
    const idLower = id.toLowerCase();
    if (idLower.includes('epirb') || idLower.includes('plb') || idLower.includes('sart')) {
      return 'LSA';
    }
    return 'GMDSS';
  }
  if (id.startsWith('machinery_')) return 'Machinery';
  if (id.startsWith('medical_')) return 'Medical';
  if (id.startsWith('electrical_')) return 'Electrical';
  if (id.startsWith('environmental_')) return 'Environmental';
  if (id.startsWith('operations_')) return 'Operations';
  
  // Content-based detection for items without clear prefixes
  const idLower = id.toLowerCase();
  
  // LSA equipment - EPIRB/PLB/SART always belong to LSA
  if (idLower.includes('epirb') || idLower.includes('plb') || idLower.includes('sart')) {
    return 'LSA';
  }
  
  // GMDSS/Communications (excluding EPIRB/PLB/SART which are LSA)
  if (idLower.includes('vhf') || idLower.includes('radio') || idLower.includes('dsc') || 
      idLower.includes('navtex') || idLower.includes('mf_radio') || idLower.includes('hf_radio')) {
    return 'GMDSS';
  }
  
  // Navigation
  if (idLower.includes('compass') || idLower.includes('gps') || idLower.includes('chart') ||
      idLower.includes('radar') || idLower.includes('ais') || idLower.includes('echo') ||
      idLower.includes('depth') || idLower.includes('sounder')) {
    return 'Navigation';
  }
  
  // Fire Fighting Equipment
  if (idLower.includes('extinguisher') || idLower.includes('fire') || idLower.includes('smoke') ||
      idLower.includes('detector') || idLower.includes('blanket') || idLower.includes('hose') ||
      idLower.includes('pump') && idLower.includes('fire')) {
    return 'FFE';
  }
  
  // Life Saving Appliances
  if (idLower.includes('lifejacket') || idLower.includes('liferaft') || idLower.includes('lifebuoy') ||
      idLower.includes('immersion') || idLower.includes('survival') || idLower.includes('ring') ||
      idLower.includes('life_') || idLower.includes('pfd') || idLower.includes('throwline')) {
    return 'LSA';
  }
  
  // Medical
  if (idLower.includes('medical') || idLower.includes('first_aid') || idLower.includes('defibrillator') ||
      idLower.includes('stretcher') || idLower.includes('oxygen') || idLower.includes('medicine')) {
    return 'Medical';
  }
  
  // Machinery
  if (idLower.includes('engine') || idLower.includes('fuel') || idLower.includes('oil') ||
      idLower.includes('pump') && !idLower.includes('fire') || idLower.includes('generator') ||
      idLower.includes('battery') || idLower.includes('starter')) {
    return 'Machinery';
  }
  
  // Environmental
  if (idLower.includes('waste') || idLower.includes('sewage') || idLower.includes('oily') ||
      idLower.includes('garbage') || idLower.includes('pollution') || idLower.includes('spill')) {
    return 'Environmental';
  }
  
  // Operations
  if (idLower.includes('winch') || idLower.includes('crane') || idLower.includes('anchor') ||
      idLower.includes('mooring') || idLower.includes('towing') || idLower.includes('deck') ||
      idLower.includes('cargo') || idLower.includes('lifting')) {
    return 'Operations';
  }
  
  // Electrical
  if (idLower.includes('light') || idLower.includes('electrical') || idLower.includes('power') ||
      idLower.includes('cable') || idLower.includes('switch') || idLower.includes('panel')) {
    return 'Electrical';
  }
  
  return 'General';
};

/**
 * Get equipment type from requirement ID
 * @param {string} id - Requirement ID
 * @returns {string} Equipment type
 */
const getItemType = (id) => {
  if (id.includes('extinguisher')) return 'extinguisher';
  if (id.includes('fixed_system')) return 'fixedSystem';
  if (id.includes('detector')) return 'detector';
  if (id.includes('blanket')) return 'blanket';
  if (id.includes('pump')) return 'pump';
  if (id.includes('hose')) return 'hose';
  return 'equipment';
};

/**
 * Get location from selection
 * @param {Object} selection - User selection
 * @returns {string} Location
 */
const getItemLocation = (selection) => {
  if (selection.location) return selection.location;
  
  const id = selection.id;
  
  // Extract location from requirement ID
  if (id.includes('_galley')) return 'galley';
  if (id.includes('_engine')) return 'engine';
  if (id.includes('_control_position')) return 'control_position';
  if (id.includes('_accommodation')) return 'accommodation';
  if (id.includes('_wheelhouse')) return 'wheelhouse';
  
  return 'general';
};

/**
 * Load user equipment selections for vessel
 * @param {string} vesselId - Current vessel ID
 * @returns {Object|null} User selections or null if none saved
 */
export const loadUserEquipmentSelections = (vesselId) => {
  if (!vesselId) return null;
  
  try {
    const selectionsKey = `equipment_selections_${vesselId}`;
    const stored = localStorage.getItem(selectionsKey);
    
    if (!stored) {
      if (process.env.NODE_ENV === 'development') {

      }
      return null;
    }
    
    const selections = JSON.parse(stored);
    
    if (process.env.NODE_ENV === 'development') {

    }
    
    return selections;
    
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {

    }
    return null;
  }
};

/**
 * Save user equipment selections for vessel
 * @param {string} vesselId - Current vessel ID
 * @param {Object} selections - User selections object
 * @returns {boolean} Success status
 */
export const saveUserEquipmentSelections = (vesselId, selections) => {
  if (!vesselId || !selections) return false;
  
  try {
    const selectionsData = {
      version: 1,
      vesselId,
      lastUpdated: new Date().toISOString(),
      selections
    };
    
    const selectionsKey = `equipment_selections_${vesselId}`;
    localStorage.setItem(selectionsKey, JSON.stringify(selectionsData));
    
    if (process.env.NODE_ENV === 'development') {

    }
    
    return true;
    
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {

    }
    return false;
  }
};

/**
 * Initialize user selections from current requirements (for first-time setup)
 * @param {Object} vesselConfig - Current vessel configuration
 * @param {string} vesselId - Current vessel ID
 * @returns {Object} Initial selections object
 */
export const initializeSelectionsFromRequirements = (vesselConfig, vesselId) => {
  if (!vesselConfig || !vesselId) {
    return { required: [], optional: [] };
  }
  
  try {
    const requirements = getHardcodedVesselRequirements(vesselConfig);
    const required = [];
    const optional = [];
    
    // Process all requirement fields
    Object.entries(requirements).forEach(([fieldName, items]) => {
      if (!Array.isArray(items)) return;
      
      items.forEach(item => {
        const selection = {
          id: item.id,
          name: item.name,
          selected: item.mandatory !== false, // Default selected if mandatory
          qtyPresent: item.mandatory !== false ? 1 : 0,
          qtyRequired: item.mandatory !== false ? 1 : 0,
          selectedAt: new Date().toISOString()
        };
        
        if (item.mandatory !== false) {
          required.push(selection);
        } else {
          optional.push(selection);
        }
      });
    });
    
    if (process.env.NODE_ENV === 'development') {

    }
    
    return { required, optional };
    
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {

    }
    return { required: [], optional: [] };
  }
};

/**
 * Get guidance key for equipment item
 * @param {string} requirementId - Equipment requirement ID
 * @returns {string|null} Guidance key if guidance available
 */
const getGuidanceKey = (requirementId) => {
  // Check ALL guidance modules for this requirement ID
  const ffeGuidance = getFFEGuidance(requirementId);
  const lsaGuidance = getLSAGuidance(requirementId);
  const navGuidance = getNAVGuidance(requirementId);
  const gmdssGuidance = getGMDSSGuidance(requirementId);
  const electricalGuidance = getElectricalGuidance(requirementId);
  const machineryGuidance = getMachineryGuidance(requirementId);
  const generalGuidance = getGeneralGuidance(requirementId);
  
  // Return the requirement ID if ANY guidance exists (consistent with hasGuidanceForRow)
  if (ffeGuidance || lsaGuidance || navGuidance || gmdssGuidance || electricalGuidance || machineryGuidance || generalGuidance) {
    return requirementId;
  }
  
  return null;
};